/**
 * Static placeholder narrative for Phase 1 of the Marketer View (SYR-68).
 *
 * Every string in this file is AI-generated copy in the eventual Phase 2 build
 * (see `project_marketer_view_syr68.md` memory). The page components bind to
 * the same `MarketerViewData` shape regardless of source, so Phase 2 swaps
 * this module for `MarketerViewAIService` output without component changes.
 *
 * The placeholders are "smart" — they're functions of the deterministic BE
 * data (booster scores, signal scores, aggregates) so the copy reads
 * plausibly per-brand rather than generic. The brand-specific punch (real
 * cause/effect commentary) is the part Phase 2 unlocks.
 *
 * TODO[SYR-68-AI]: Replace every export here with a call to
 * MarketerViewAIService.<slice> from the data hook.
 */

import type {
  Band,
  BoosterScore,
  HeroVerdict,
  LeverCard,
  Play,
  ScoreNode,
} from '../types';
import { BAND_PALETTE, formatBipolarScore } from './bandColours';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const BAND_RANK: Record<Band, number> = {
  strong: 3,
  watch: 2,
  weak: 1,
  na: 0,
};

function topByValue<T extends { value: number | null }>(
  list: readonly T[],
  n: number,
): T[] {
  return [...list]
    .filter((s) => s.value != null)
    .sort((a, b) => (b.value ?? 0) - (a.value ?? 0))
    .slice(0, n);
}

function bottomByValue<T extends { value: number | null }>(
  list: readonly T[],
  n: number,
): T[] {
  return [...list]
    .filter((s) => s.value != null)
    .sort((a, b) => (a.value ?? 0) - (b.value ?? 0))
    .slice(0, n);
}

// ---------------------------------------------------------------------------
// Hero verdict
// ---------------------------------------------------------------------------

/**
 * Build a hero verdict tailored to whether the brand is "loved by few /
 * invisible to many" (high love, low know) or has a different shape.
 *
 * TODO[SYR-68-AI]: replace with MarketerViewAIService.headline output.
 */
export function buildHeroVerdict(args: {
  brandName: string;
  /** Bipolar -100..+100 average of the love-group signals. */
  strengthAvg: number;
  /** Bipolar -100..+100 average of the know-group signals. */
  visibilityAvg: number;
}): Pick<HeroVerdict, 'headline' | 'body'> & {
  strengthCaption: string;
  visibilityCaption: string;
} {
  const gap = args.strengthAvg - args.visibilityAvg;
  // Thresholds: bipolar +20 ≈ "Good" / "above neutral".
  // bipolar -20 ≈ "Needs Attention" / "below neutral".
  const lovedNotKnown = args.strengthAvg >= 20 && args.visibilityAvg < -20;
  const knownNotLoved = args.visibilityAvg >= 20 && args.strengthAvg < -20;
  const both = args.strengthAvg >= 20 && args.visibilityAvg >= 20;

  let headline = `${args.brandName}: your brand at a glance.`;
  let body = `The numbers below show where the brand is strong, where it is exposed, and what to act on first. Open any signal for the detail.`;

  if (lovedNotKnown) {
    headline = `You're loved by the few, and invisible to the many.`;
    body = `The people who know ${args.brandName} connect deeply, rate the product highly and tell others about it. The problem is almost no one in the open market knows you exist, so paid media is doing all the persuading, and you're paying full price for every customer.`;
  } else if (knownNotLoved) {
    headline = `Plenty of awareness, not enough love.`;
    body = `The brand is visible in the market, but the people you reach aren't bonding with it. New customers cost less to find than to keep. The fix lives on the product and experience side, not the media side.`;
  } else if (both) {
    headline = `Strong on both sides: a brand ready to scale.`;
    body = `${args.brandName} is felt strongly by those who know it AND meaningfully present in the wider market. Focus turns to defending that position and removing the small drags.`;
  } else if (gap > 20) {
    headline = `The gap between knowing and loving you is the opportunity.`;
    body = `Strength among the people who do know ${args.brandName} is materially higher than visibility in the open market. Closing that gap converts existing fans into a free megaphone, and is the cheapest growth you have.`;
  }

  return {
    headline,
    body,
    strengthCaption: 'Attachment · advocacy · product love',
    visibilityCaption: 'Familiarity · trust · salience',
  };
}

// ---------------------------------------------------------------------------
// Booster commentary (the `read` line per booster + Q1 footnote)
// ---------------------------------------------------------------------------

const BAND_READ_PHRASE: Record<Band, string> = {
  strong: 'a real strength to lean on.',
  watch: 'sitting in the middle, neither a drag nor a lever yet.',
  weak: 'one of the drags, limiting equity directly.',
  na: 'not yet measured: gather data before drawing conclusions.',
};

/**
 * Per-booster one-line interpretation, defaulting on band. The Phase 2 AI
 * version produces brand-specific copy ("Your strongest force. The few who
 * buy form a genuine, identity-level bond.") rather than band-templated text.
 */
export function buildBoosterRead(b: BoosterScore): string {
  return `${b.axis}: ${BAND_READ_PHRASE[b.band]}`;
}

export function buildBoostersFootnote(boosters: readonly BoosterScore[]): string {
  const measured = boosters.filter((b) => b.value != null);
  const strong = measured.filter((b) => b.band === 'strong');
  const weak = measured.filter((b) => b.band === 'weak');
  const unmeasured = boosters.filter((b) => b.value == null);

  const parts: string[] = [];
  if (strong.length > 0) {
    parts.push(
      `${strong.length} of ${boosters.length} are healthy, anchored by ${strong
        .slice(0, 2)
        .map((b) => b.display)
        .join(' and ')}.`,
    );
  }
  if (weak.length > 0) {
    parts.push(
      `The drags are ${weak.map((b) => b.display).join(' and ')}. Fix these first.`,
    );
  }
  if (unmeasured.length > 0) {
    parts.push(
      `${unmeasured.map((b) => b.display).join(', ')} ${
        unmeasured.length === 1 ? 'is' : 'are'
      } unmeasured.`,
    );
  }
  return parts.join(' ') ||
    'Booster picture is mixed. Open each booster to see the detail.';
}

/**
 * A few words of an element's AI "First Thoughts" for the Q3/Q4 hero cards —
 * markdown stripped, first ~`maxWords` words, with an ellipsis when truncated.
 */
export function firstThoughtsSnippet(
  text: string | null | undefined,
  maxWords = 12,
): string {
  if (!text) return '';
  const plain = text
    .replace(/[*_`#>~]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  const words = plain.split(' ');
  if (words.length <= maxWords) return plain;
  return `${words.slice(0, maxWords).join(' ')}…`;
}

// ---------------------------------------------------------------------------
// Signal interpretation (the `note` line per signal)
// ---------------------------------------------------------------------------

const SIGNAL_NOTES_BY_BAND: Record<Band, string> = {
  strong: 'A clear positive. Leverage it.',
  watch: 'Moderate. Keep an eye on it.',
  weak: 'A drag. Surface it in the action plan.',
  na: 'Not yet measured.',
};

export function buildSignalNote(node: { band: Band }): string {
  return SIGNAL_NOTES_BY_BAND[node.band];
}

// ---------------------------------------------------------------------------
// Q2 / Q3 framing copy
// ---------------------------------------------------------------------------

export function buildWorkingFraming(strongSignals: readonly ScoreNode[]): string {
  const top = topByValue(strongSignals, 3);
  if (top.length === 0) {
    return `Nothing is scoring in the strong band yet. Collect more data or check the underlying signals for gaps.`;
  }
  return `${top
    .map((s) => s.display)
    .join(', ')} are the signals currently scoring strongest: the strengths to lean on in your story.`;
}

export function buildBrokenFraming(weakSignals: readonly ScoreNode[]): string {
  const bottom = bottomByValue(weakSignals, 3);
  if (bottom.length === 0) {
    return `No signals are sitting in the weak band right now. Nothing is actively dragging on equity. Strong work.`;
  }
  return `${bottom
    .map((s) => s.display)
    .join(', ')} are the weakest signals. These are dragging on overall equity and should lead your fix list.`;
}

// ---------------------------------------------------------------------------
// Fix-first plays (Q4) — placeholder generator
// ---------------------------------------------------------------------------

/**
 * Build 3 Fix-first plays from the weakest signals.
 *
 * TODO[SYR-68-AI]: replace with MarketerViewAIService.plays output, which
 * clusters weakest signals by underlying cause and writes brand-specific
 * cause/effect copy. Phase 1 just picks the three weakest with a templated
 * move/effect for shape.
 */
export function buildFixFirstPlays(weakest: readonly ScoreNode[]): Play[] {
  return weakest.slice(0, 3).map((s, i) => ({
    id: `fix-${i + 1}`,
    tag: `Fix ${i + 1}`,
    title: `Improve ${s.display}`,
    lead: `${s.display} is dragging on equity. Addressing it lifts multiple downstream signals.`,
    problem: `${s.display} sits at the weak end. Customers feel its absence.`,
    move: `Identify the operational lever behind ${s.display} and put a focused, time-boxed initiative against it. Treat it as the single thing to move this quarter.`,
    effect:
      'Lifts the related signals on the chart and removes a drag on overall equity.',
    metrics: [s.canonical],
    tone:
      i === 0
        ? BAND_PALETTE.weak.fg
        : i === 1
          ? BAND_PALETTE.watch.fg
          : '#3B6FD4',
    objectives: [
      {
        id: `fix-${i + 1}-task-1`,
        title: `Diagnose the root cause of ${s.display}`,
        how: `Interview 5 recent customers and 5 churned ones; map the moments where ${s.display} signals form.`,
        impact: 'Med',
        effort: 'Low',
        metric: s.canonical,
      },
      {
        id: `fix-${i + 1}-task-2`,
        title: `Pick one operational change and ship it`,
        how: `From the diagnosis, isolate the highest-leverage intervention and put a 4-week initiative behind it.`,
        impact: 'High',
        effort: 'Med',
        metric: s.canonical,
      },
    ],
  }));
}

// ---------------------------------------------------------------------------
// Opportunity plays (Opportunities tab)
// ---------------------------------------------------------------------------

/**
 * The opportunities list extends the Fix-first set with a Quick win drawn
 * from the most actionable negative signal (defaults to Price Deals if it
 * appears as a drag — discounting is almost always the quickest tactical fix).
 */
export function buildOpportunities(args: {
  fixFirstPlays: readonly Play[];
  signals: readonly ScoreNode[];
}): Play[] {
  const priceDeals = args.signals.find((s) => s.canonical === 'Price Deals');
  const quickWin: Play | null =
    priceDeals && priceDeals.band === 'weak'
      ? {
          id: 'qw-1',
          tag: 'Quick win',
          title: 'Stop discount-led erosion',
          lead: 'Stops active harm. Low effort, do it this week.',
          problem:
            'Discounting is the one force pulling equity down. It trains your best customers to wait.',
          move: 'Pause promo-driven sends and let full-price demand surface. Re-route the calendar to brand campaigns.',
          effect: 'Removes the only actively-negative signal on the board.',
          metrics: ['Price Deals'],
          tone: BAND_PALETTE.strong.fg,
          objectives: [
            {
              id: 'qw-1-task-1',
              title: 'Pause the next discount window',
              how: 'Skip the next 2 sale moments; replace with brand storytelling.',
              impact: 'Med',
              effort: 'Low',
              metric: 'Price Deals',
            },
          ],
        }
      : null;
  return quickWin ? [quickWin, ...args.fixFirstPlays] : [...args.fixFirstPlays];
}

// ---------------------------------------------------------------------------
// Q5 — lever cards + framing + one-line plan
// ---------------------------------------------------------------------------

export function buildQ5(args: {
  brandName: string;
  signals: readonly ScoreNode[];
}): { framing: string; levers: LeverCard[]; oneLinePlan: string } {
  const advocacy = args.signals.find((s) => s.canonical === 'Standardised NPS');
  const repurchase = args.signals.find(
    (s) => s.canonical === 'Repurchase Intention',
  );
  const availability = args.signals.find(
    (s) => s.canonical === 'Distribution Intensity',
  );
  const productPerf = args.signals.find(
    (s) => s.canonical === 'Product Performance',
  );
  const priceDeals = args.signals.find((s) => s.canonical === 'Price Deals');
  const wtp = args.signals.find(
    (s) => s.canonical === 'Willingness to Pay a Premium',
  );
  const pqv = args.signals.find(
    (s) => s.canonical === 'Quality Value Tradeoff (PQV)',
  );

  const levers: LeverCard[] = [
    {
      id: 'lower-cac',
      title: 'Lower your CAC',
      icon: '↓',
      tone: BAND_PALETTE.strong.fg,
      points: [
        {
          title: advocacy
            ? `Activate advocacy (${advocacy.display})`
            : 'Activate advocacy',
          body: 'Build a referral + UGC loop. Advocates acquire customers at a fraction of paid CAC.',
        },
        {
          title: repurchase
            ? `Bank repurchase (${repurchase.display})`
            : 'Bank repurchase intent',
          body: 'Strong repeat intent means LTV is high. You can afford to acquire, and retention compounds.',
        },
        {
          title: 'Brand-led demand',
          body: 'Salience makes people search and arrive direct, cutting reliance on rising auction prices.',
        },
      ],
    },
    {
      id: 'drive-sales',
      title: 'Drive more sales',
      icon: '↑',
      tone: '#3B6FD4',
      points: [
        {
          title: availability
            ? `Unlock distribution (${availability.display})`
            : 'Unlock distribution',
          body: 'Every new place to encounter and buy is incremental revenue, not cannibalised.',
        },
        {
          title: 'Convert the fuzzy middle',
          body: `Sharper associations turn "who are they?" into "oh, them", lifting conversion on existing traffic.`,
        },
        {
          title: productPerf
            ? `Scale the proven product (${productPerf.display})`
            : 'Scale the proven product',
          body: 'Strong product performance means more buyers stick. Growth doesn’t leak out the bottom.',
        },
      ],
    },
    {
      id: 'protect-margin',
      title: 'Protect your margin',
      icon: '$',
      tone: BAND_PALETTE.watch.fg,
      points: [
        {
          title: priceDeals
            ? `Stop discounting (${priceDeals.display})`
            : 'Stop discounting',
          body: 'Discounting is the only force pulling equity down. It is training your best customers to wait.',
        },
        {
          title: wtp ? `Charge the premium (${wtp.display})` : 'Charge the premium',
          body: 'Customers will pay more. Full-price demand is sitting unclaimed.',
        },
        {
          title: pqv ? `Value is already believed (${pqv.display})` : 'Value is already believed',
          body: 'People feel they get their money’s worth. You don’t need price to win.',
        },
      ],
    },
  ];

  return {
    framing: `A loved-but-unknown brand is the best CAC setup there is: ${args.brandName} has a pre-sold audience and not enough megaphone. Three levers convert equity into growth.`,
    levers,
    oneLinePlan: `Spend on being remembered, not on being persuasive. The product already persuades. Buy salience, kill discounts, and let a pre-sold audience do the closing.`,
  };
}

// ---------------------------------------------------------------------------
// Explore-tab theme verdicts
// ---------------------------------------------------------------------------

const THEME_VERDICT_NA =
  'Limited data for this area yet. Gather more responses before drawing conclusions.';

/**
 * Deterministic, brand-specific theme verdict: the dominant band sets the
 * framing, and the actual strongest/weakest signals (with scores) make it
 * concrete — no AI needed.
 */
export function buildThemeVerdict(signals: readonly ScoreNode[]): string {
  const measured = signals.filter((s) => s.value != null);
  if (measured.length === 0) return THEME_VERDICT_NA;

  const sorted = [...measured].sort(
    (a, b) => (b.value as number) - (a.value as number),
  );
  const top = sorted[0];
  const bottom = sorted[sorted.length - 1];
  const single = top === bottom;
  const hasWeak = sorted.some((s) => s.band === 'weak');
  const named = (n: ScoreNode): string => `${n.display} (${formatBipolarScore(n.value)})`;

  const counts: Record<Band, number> = { strong: 0, watch: 0, weak: 0, na: 0 };
  for (const s of measured) counts[s.band] += 1;
  const dominant = (['strong', 'watch', 'weak', 'na'] as Band[]).reduce((a, b) =>
    counts[b] > counts[a] ? b : a,
  );

  switch (dominant) {
    case 'strong':
      if (single) {
        return `Healthy area: ${named(top)} is strong. Lean on it in your storytelling.`;
      }
      return hasWeak
        ? `Healthy area: anchored by ${named(top)}; only ${named(bottom)} holds it back. Lean on the strengths in your storytelling.`
        : `Healthy area: anchored by ${named(top)}, with the rest close behind. Lean on it in your storytelling.`;
    case 'weak':
      return single
        ? `A drag on equity: ${named(top)} is weak here. Make it a fix-list priority.`
        : `A drag on equity: ${named(bottom)} is the weakest and most signals here sit below par. Make this a fix-list priority.`;
    case 'na':
      return THEME_VERDICT_NA;
    case 'watch':
    default:
      return single
        ? `Mixed picture: ${named(top)} sits mid-range. Open the tile to dig in.`
        : `Mixed picture: ${named(top)} leads while ${named(bottom)} needs work. Open the tiles to see which way each is pulling.`;
  }
}

// Exported so the orbital ModelTab can colour engines using the same
// band-rank logic as the rest of the placeholder.
export const _BAND_RANK = BAND_RANK;
